import { motion } from 'framer-motion';
import { GraphQLError } from 'graphql';
import { t } from 'i18next';
import { useRef, useState } from 'react';
import { Box, Flex } from 'reflexbox';

import { useFeature, useViewer } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	FlexGap,
	GraphQLErrorMessage,
	IconWrapper,
	Paragraph,
	QuillText,
	Separator,
	Spinner,
	StarsIcon,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import Comments from '../comments/Comments';
import { CommentComposerHandle } from '../comments/components/CommentComposer';
import { FeatureRatingSection } from '../feature-ratings/FeatureRatingSection';
import NoteTranscription from './NoteTranscription';
import NotesMetaEditContainer from './NotesMetaEditContainer';
import NotePreparationPanel from './one-on-ones/NotePreparationPanel';
import NoteTasksPanel from './one-on-ones/NoteTasksPanel';
import NoteWeekAgenda from './one-on-ones/NoteWeekAgenda';
import TemplateGrid from './one-on-ones/templates/TemplateGrid';

const RATING_SAMPLE_RATE = 1 / 3;

export function NotesDetailContainerWithoutSidebar(props: { id: string; error?: GraphQLError }) {
	const {
		data,
		loading,
		refetch,
		error: noteQueryError,
	} = graphql.useNoteQuery({
		variables: {
			id: props.id,
		},
	});
	const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);
	const hasAI = useFeature(graphql.FeatureName.Ai);
	const hasAIInsights = useFeature(graphql.FeatureName.ConversationAiInsights);
	const hasTranscriptions = useFeature(graphql.FeatureName.ConversationTranscriptions);
	const viewer = useViewer();

	const findError = () => {
		if (props.error) {
			return props.error;
		}

		if (noteQueryError) {
			return noteQueryError.graphQLErrors[0];
		}
		return undefined;
	};

	const note = data?.note as graphql.Note | undefined;

	const tightWidth = [1, 1, 1, 3 / 4];
	const tightMaxWidth = ['auto', 'auto', 'auto', '700px'];

	const [commentCount, setCommentCount] = useState<number>();
	const [hasReceivedInsight, setHasReceivedInsight] = useState(false);
	const isEmpty = !!note && !note.content && commentCount !== undefined && commentCount === 0;
	const isOwner = note?.owner.id === viewer?.id;
	const hasSubject = !!note?.subject;

	const showOneOnOneTemplates =
		isEmpty &&
		isOwner &&
		hasAI &&
		hasAIInsights &&
		hasOneOnOnes &&
		hasSubject &&
		!hasReceivedInsight;
	const showSidebar = hasOneOnOnes && !!note && !!note.subject;

	const { data: templatesData } = graphql.useNoteTemplatesQuery({
		variables: { isArchived: false, first: 6, subjectID: note?.subject?.id },
		skip: !showOneOnOneTemplates,
	});
	const templates = templatesData?.noteTemplates.edges.map((edge) => edge.node) ?? [];

	const composerRef = useRef<CommentComposerHandle>(null);

	return (
		<>
			<GraphQLErrorMessage error={findError()} />
			{loading && !note && (
				<Flex justifyContent="center" alignItems="center" my={4}>
					<Spinner />
				</Flex>
			)}
			{note && (
				<>
					<Box mb={4}>
						<NotesMetaEditContainer note={note} />
					</Box>
					{!hasOneOnOnes && <Separator />}
					<motion.div
						initial={false}
						animate={{
							height: showOneOnOneTemplates ? 'auto' : 0,
							opacity: showOneOnOneTemplates ? 1 : 0,
							overflow: showOneOnOneTemplates ? 'visible' : 'hidden',
						}}
					>
						{note.subject && templates.length > 0 && hasAIInsights && (
							<Box py={4}>
								<FlexGap gap={tokens.gap.element} flexDirection="column" mb={3}>
									<FlexGap gap={tokens.gap.element} alignItems="center">
										<IconWrapper size={20} color={tokens.brand.icon}>
											<StarsIcon color={tokens.brand.icon} />
										</IconWrapper>
										<Subheading style={{ color: tokens.brand.text }}>
											{t('oneOnOnes.templates.intro.heading')}
										</Subheading>
									</FlexGap>
									<Paragraph maxWidth={420} style={{ color: tokens.brand.text }}>
										{t('oneOnOnes.templates.intro.explanation')}
									</Paragraph>
								</FlexGap>
								<TemplateGrid
									templates={templates}
									noteID={note.id}
									subjectUserID={note.subject.id}
									composerRef={composerRef}
									onInsightReceived={() => setHasReceivedInsight(true)}
								/>
							</Box>
						)}
					</motion.div>
					{hasTranscriptions && note.transcription && (
						<Box my={4}>
							<NoteTranscription note={note} onRefresh={refetch} />
						</Box>
					)}
					<Flex
						justifyContent="flex-start"
						alignItems="flex-start"
						flexDirection={showSidebar ? ['column', 'column', 'row'] : 'column'}
						style={{ gap: tokens.gap.group }}
					>
						<Box
							flex={showSidebar ? '1 1 auto' : undefined}
							minWidth={0}
							width={hasOneOnOnes && !showSidebar ? [1] : tightWidth}
							maxWidth={hasOneOnOnes && !showSidebar ? undefined : tightMaxWidth}
						>
							{note.content && (
								<Box mb={3}>
									<QuillText value={note.content} />
								</Box>
							)}
							<Box mb={4}>
								<Comments
									target={note}
									alignComposer="top"
									composerRef={composerRef}
									composerSecondaryAction={
										hasTranscriptions && !note.transcription ? (
											<NoteTranscription
												note={note}
												onRefresh={refetch}
												variant="inlineRecordButton"
											/>
										) : undefined
									}
									onCommentsLoaded={(comments) => setCommentCount(comments.length)}
									hideEmptyState
								/>
							</Box>
							{hasReceivedInsight && (
								<Box mb={4}>
									<FeatureRatingSection
										featureName={graphql.FeatureName.ConversationAiInsights}
										targetID={note.id}
										targetType={graphql.FeatureRatingTargetType.NoteTalkingPoints}
										i18nPrefix="feature.ratings.ai.talkingPoints"
										sampleRate={RATING_SAMPLE_RATE}
									/>
								</Box>
							)}
						</Box>
						{showSidebar && (
							<FlexGap
								width={['100%', '100%', '300px']}
								flexShrink={0}
								flexDirection="column"
								gap={tokens.gap.list}
							>
								{note.subject && <NoteWeekAgenda note={note} subject={note.subject} />}
								<NotePreparationPanel key={note.id} note={note} />
								<NoteTasksPanel
									noteId={note.id}
									tasks={note.tasks}
									onRefetch={refetch}
									readOnly={!isOwner}
								/>
							</FlexGap>
						)}
					</Flex>
				</>
			)}
		</>
	);
}
